import React, { useState, useMemo, useEffect } from "react";
import Swal from "sweetalert2";
import { X, Plus } from "lucide-react";
import ModalFuncionalidades from "../../components/funcionalidadesEvento/ModalFuncionalidades";
import "./CriarEvento.scss";

import { upsertEvento } from "../../services/eventosStore";
import { listarTurmas } from "../../api/turmas";
import { usePermissaoEventos } from "../../hooks/usePermissaoEventos";
import { useDisciplinaAtual } from "../../hooks/useDisciplinaAtual";

const MSG_SEM_PERMISSAO = "Este usuário não pode efetuar essa ação.";

const TITULO_MAX = 60;
const DESCRICAO_MAX = 800;

function capitalizeFirst(str) {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function getUsuarioLogado() {
  try {
    const u = JSON.parse(localStorage.getItem("usuario")) || null;
    if (!u) return null;
    // Normaliza payload backend (instituicaoId, tipo PascalCase) → shape legado
    return {
      ...u,
      faculdadeId: u.faculdadeId || u.instituicaoId,
      tipo: String(u.tipo || "").toLowerCase(),
    };
  } catch {
    return null;
  }
}

function getDisciplinaAtualId() {
  return localStorage.getItem("disciplinaAtualId") || "";
}

function getCoordenacaoAtualId() {
  return localStorage.getItem("coordenacaoAtualId") || "";
}

// Título exibido = nome + complemento (ex: "Cálculo 1 - Turma 11").
function nomeTurmaCompleto(t) {
  const nome = String(t?.nome || "").trim();
  const comp = String(t?.complemento || "").trim();
  return comp ? `${nome} - ${comp}` : nome;
}

const CriarEvento = () => {
  const [titulo, setTitulo] = useState("");
  const [descricao, setDescricao] = useState("");
  const [startDate, setStartDate] = useState(null);

  const [notificacoes, setNotificacoes] = useState({
    calendario: false,
    destaque: false,
  });

  const [confirmacaoPresenca, setConfirmacaoPresenca] = useState(false);
  const [modalFuncAberto, setModalFuncAberto] = useState(false);

  // mantém o mesmo comportamento visual do design
  const [modoTurmas, setModoTurmas] = useState("all"); // "all" | "some"
  const [turmaSelecionadaId, setTurmaSelecionadaId] = useState("");
  const [turmaIds, setTurmaIds] = useState([]);

  const tituloCount = useMemo(() => titulo.length, [titulo]);
  const descricaoCount = useMemo(() => descricao.length, [descricao]);

  const usuario = useMemo(() => getUsuarioLogado(), []);
  const isCoordenador = (usuario?.tipo || "").toLowerCase() === "coordenador";

  // Permissão de criar eventos é por turma. Coordenador sempre pode; demais
  // dependem do vínculo na turma de contexto (disciplinaAtualId, reativo).
  const turmaContexto = useDisciplinaAtual();
  const { pode: podeCriarEventos } = usePermissaoEventos(turmaContexto);
  const bloqueado = !podeCriarEventos;

  const avisarBloqueio = () => {
    Swal.fire({
      title: "Ação não permitida",
      text: MSG_SEM_PERMISSAO,
      icon: "info",
      confirmButtonText: "Entendi",
      confirmButtonColor: "#2E4A67",
    });
  };

  const instituicaoId = useMemo(
    () => usuario?.faculdadeId || usuario?.instituicaoId || null,
    [usuario],
  );

  const [turmasApi, setTurmasApi] = useState([]);
  const [coordenacaoAtualId, setCoordenacaoAtualId] = useState(
    getCoordenacaoAtualId,
  );

  // Turmas disponíveis = vinculadas à coordenação atual (N:N), via backend.
  useEffect(() => {
    if (!isCoordenador || !instituicaoId) {
      setTurmasApi([]);
      return;
    }
    let vivo = true;
    (async () => {
      try {
        const lista = await listarTurmas(instituicaoId);
        if (!vivo) return;
        const filtradas = (Array.isArray(lista) ? lista : []).filter((t) => {
          if (!coordenacaoAtualId) return true;
          const ids = Array.isArray(t.coordenacaoIds) ? t.coordenacaoIds : [];
          return ids.some((id) => String(id) === String(coordenacaoAtualId));
        });
        setTurmasApi(
          filtradas
            .map((t) => ({ id: t.id, nome: nomeTurmaCompleto(t) }))
            .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR")),
        );
      } catch {
        if (vivo) setTurmasApi([]);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [isCoordenador, instituicaoId, coordenacaoAtualId]);

  useEffect(() => {
    const onCoord = () => setCoordenacaoAtualId(getCoordenacaoAtualId());
    window.addEventListener("coordenacaoAtual:changed", onCoord);
    window.addEventListener("turmas:changed", onCoord);
    return () => {
      window.removeEventListener("coordenacaoAtual:changed", onCoord);
      window.removeEventListener("turmas:changed", onCoord);
    };
  }, []);

  const turmasDisponiveis = useMemo(() => {
    if (!isCoordenador) return [];
    return turmasApi;
  }, [isCoordenador, turmasApi]);

  const turmaIdToNome = useMemo(() => {
    const m = new Map();
    turmasDisponiveis.forEach((t) => m.set(String(t.id), t.nome));
    return m;
  }, [turmasDisponiveis]);

  const setAllTurmas = () => {
    setModoTurmas("all");
    setTurmaSelecionadaId("");
    setTurmaIds([]);
  };

  const setSomeTurmas = () => {
    setModoTurmas("some");
    setTurmaSelecionadaId("");
  };

  const addTurma = () => {
    if (modoTurmas !== "some") return;

    const id = turmaSelecionadaId;
    if (!id) return;

    setTurmaIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    setTurmaSelecionadaId("");
  };

  const removeTurma = (id) => {
    setTurmaIds((prev) => prev.filter((x) => String(x) !== String(id)));
  };

  function resolverDisciplinaIds({
    isCoordenador,
    modoTurmas,
    turmaIds,
    turmasDisponiveis,
    disciplinaAtual,
  }) {
    if (!isCoordenador) {
      return disciplinaAtual ? [disciplinaAtual] : [];
    }

    if (modoTurmas === "all") {
      return turmasDisponiveis.map((t) => t.id).filter(Boolean);
    }

    return Array.from(new Set((turmaIds || []).filter(Boolean)));
  }

  function resolverDestino({ isCoordenador, modoTurmas, disciplinaIds }) {
    if (!isCoordenador) return "uma";
    if (modoTurmas === "all") return "todas";
    if ((disciplinaIds || []).length <= 1) return "uma";
    return "varias";
  }

  const handleSalvar = async () => {
    if (bloqueado) {
      avisarBloqueio();
      return;
    }
    if (!titulo.trim()) {
      Swal.fire({
        title: "Campos obrigatórios",
        text: "Por favor, preencha o título do evento.",
        icon: "warning",
        confirmButtonText: "Entendido",
        confirmButtonColor: "#2E4A67",
      });
      return;
    }

    if (!descricao.trim()) {
      Swal.fire({
        title: "Campos obrigatórios",
        text: "Por favor, preencha a descrição do evento.",
        icon: "warning",
        confirmButtonText: "Entendido",
        confirmButtonColor: "#2E4A67",
      });
      return;
    }

    if (!notificacoes.calendario && !notificacoes.destaque) {
      Swal.fire({
        title: "Campos obrigatórios",
        text: "Selecione o tipo de notificação do evento.",
        icon: "warning",
        confirmButtonText: "Entendido",
        confirmButtonColor: "#2E4A67",
      });
      return;
    }

    if (notificacoes.calendario && !startDate) {
      Swal.fire({
        title: "Campos obrigatórios",
        text: "Selecione a data do evento para aviso no calendário.",
        icon: "warning",
        confirmButtonText: "Entendido",
        confirmButtonColor: "#2E4A67",
      });
      return;
    }

    if (isCoordenador && modoTurmas === "some" && turmaIds.length === 0) {
      Swal.fire({
        title: "Selecione ao menos uma turma",
        text: "Você está em 'Selecionar'. Adicione pelo menos uma turma ou troque para 'Todas'.",
        icon: "warning",
        confirmButtonText: "Ok",
        confirmButtonColor: "#2E4A67",
      });
      return;
    }

    try {
      Swal.fire({
        title: "Publicando evento...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading(),
      });

      // backend gera Guid no insert
      const dataEventoISO = notificacoes.calendario ? startDate : null;

      const agoraISO = new Date().toISOString();

      const usuarioNow = getUsuarioLogado();
      const criadoPorId = usuarioNow?.id;

      if (!criadoPorId) {
        Swal.close();
        await Swal.fire({
          title: "Sessão expirada",
          text: "Faça login novamente para publicar o evento.",
          icon: "warning",
          confirmButtonText: "Ok",
          confirmButtonColor: "#2E4A67",
        });
        return;
      }

      const disciplinaAtual = getDisciplinaAtualId() || null;
      if (!disciplinaAtual && !isCoordenador) {
        Swal.close();
        await Swal.fire({
          title: "Selecione uma disciplina",
          text: "Escolha uma disciplina antes de criar o evento.",
          icon: "warning",
          confirmButtonText: "Ok",
          confirmButtonColor: "#2E4A67",
        });
        return;
      }

      const instId =
        usuarioNow?.faculdadeId || usuarioNow?.instituicaoId || null;
      if (!instId) {
        Swal.close();
        await Swal.fire({
          title: "Usuário sem instituição",
          text: "Não foi possível identificar a instituição do usuário logado.",
          icon: "warning",
          confirmButtonText: "Ok",
          confirmButtonColor: "#2E4A67",
        });
        return;
      }

      const disciplinaIds = resolverDisciplinaIds({
        isCoordenador,
        modoTurmas,
        turmaIds,
        turmasDisponiveis,
        disciplinaAtual,
      });

      if (!disciplinaIds.length) {
        Swal.close();
        await Swal.fire({
          title: "Selecione ao menos uma turma",
          text: "Escolha a turma do evento antes de publicar.",
          icon: "warning",
          confirmButtonText: "Ok",
          confirmButtonColor: "#2E4A67",
        });
        return;
      }

      const destino = resolverDestino({
        isCoordenador,
        modoTurmas,
        disciplinaIds,
      });

      const novoEvento = {
        titulo: titulo.trim(),
        descricao: descricao.trim(),
        ultimaAtualizacao: agoraISO,
        dataEvento: dataEventoISO ?? null,
        criadoPorId,
        instituicaoId: instId,
        disciplinaIds,
        destino,
        calendario: notificacoes.calendario,
        destaque: notificacoes.destaque,
        confirmacaoPresenca: notificacoes.calendario && confirmacaoPresenca,
      };

      await upsertEvento(novoEvento);

      Swal.close();

      await Swal.fire({
        title: "Sucesso!",
        text: "O evento foi publicado e já está disponível.",
        icon: "success",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });

      setTitulo("");
      setDescricao("");
      setStartDate(null);
      setNotificacoes({ calendario: false, destaque: false });
      setConfirmacaoPresenca(false);
      setAllTurmas();
    } catch (error) {
      Swal.close();
      Swal.fire({
        title: "Erro ao publicar",
        text: "Não foi possível salvar o evento. Tente novamente em instantes.",
        icon: "error",
        confirmButtonText: "Fechar",
        confirmButtonColor: "#d33",
      });
    }
  };

  return (
    <div
      className={`painel-evento ${isCoordenador ? "criar-evento-coordenador-page" : ""}`}
    >
      <div
        className={`eventos-wrap ${isCoordenador ? "eventos-wrap--coordenador" : ""} ${
          bloqueado ? "eventos-wrap--bloqueado" : ""
        }`}
      >
        {bloqueado && (
          // Overlay captura cliques em qualquer campo/aba e mostra o aviso.
          <div
            className="permissao-overlay"
            onClick={avisarBloqueio}
            role="button"
            tabIndex={0}
            aria-label={MSG_SEM_PERMISSAO}
          />
        )}
        <section
          className={`card-bloco ${
            isCoordenador ? "criar-evento-card criar-evento-card--top" : ""
          }`}
        >
          <h1 className={isCoordenador ? "page-title" : "titulo-sessao"}>
            Criar novo evento
          </h1>

          <div
            className={
              isCoordenador
                ? "formulario formulario--coordenador"
                : "formulario"
            }
          >
            <div className={isCoordenador ? "field" : "campo"}>
              <label className="titulo-evento">Título</label>
              <input
                type="text"
                placeholder="Digite o título do evento"
                className="input-estilizado"
                value={titulo}
                maxLength={TITULO_MAX}
                onChange={(e) => {
                  const raw = e.target.value || "";
                  const limited = raw.slice(0, TITULO_MAX);
                  setTitulo(capitalizeFirst(limited));
                }}
                required
              />
              <div className="contador-campo">
                {tituloCount}/{TITULO_MAX}
              </div>
            </div>

            <div className={isCoordenador ? "field" : "campo"}>
              <label>Descrição</label>
              <textarea
                rows="10"
                placeholder="Descreva os detalhes do evento"
                className="input-estilizado"
                value={descricao}
                maxLength={DESCRICAO_MAX}
                onChange={(e) => {
                  const raw = e.target.value || "";
                  setDescricao(raw.slice(0, DESCRICAO_MAX));
                }}
                required
              />
              <div className="contador-campo">
                {descricaoCount}/{DESCRICAO_MAX}
              </div>
            </div>
          </div>
        </section>

        {isCoordenador && (
          <section className="card-bloco criar-evento-card">
            <h2 className="section-title">Turmas</h2>

            <div className="turmas-toggle">
              <button
                type="button"
                className={`turmas-toggle__btn ${modoTurmas === "all" ? "is-active" : ""}`}
                onClick={setAllTurmas}
              >
                Todas
              </button>

              <button
                type="button"
                className={`turmas-toggle__btn ${modoTurmas === "some" ? "is-active" : ""}`}
                onClick={setSomeTurmas}
              >
                Selecionar
              </button>
            </div>

            <div className="field" style={{ marginBottom: 0 }}>
              <div className="turmas-select-row">
                <div className="select-wrap">
                  <select
                    className="select-estilizado"
                    value={turmaSelecionadaId}
                    onChange={(e) => setTurmaSelecionadaId(e.target.value)}
                    disabled={modoTurmas === "all"}
                  >
                    <option value="" disabled>
                      Selecionar turma
                    </option>

                    {turmasDisponiveis
                      .filter(
                        (t) =>
                          !turmaIds.some((x) => String(x) === String(t.id)),
                      )
                      .map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.nome}
                        </option>
                      ))}
                  </select>

                  <span className="select-arrow" aria-hidden="true">
                    ▾
                  </span>
                </div>

                <button
                  type="button"
                  className="btn-add-turma"
                  onClick={addTurma}
                  disabled={modoTurmas === "all" || !turmaSelecionadaId}
                >
                  Adicionar
                </button>
              </div>

              {modoTurmas === "some" && turmaIds.length > 0 && (
                <div className="turmas-chips">
                  {turmaIds.map((id) => (
                    <div className="chip-turma" key={id}>
                      <span className="chip-text">
                        {turmaIdToNome.get(String(id)) || `Turma ${id}`}
                      </span>
                      <button
                        type="button"
                        className="chip-remove"
                        onClick={() => removeTurma(id)}
                        aria-label="Remover turma"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {modoTurmas === "all" && (
                <div className="turmas-info">
                  Todas as turmas serão selecionadas.
                </div>
              )}
            </div>
          </section>
        )}

        <section
          className={`card-bloco ${isCoordenador ? "criar-evento-card" : ""}`}
        >
          {isCoordenador ? (
            <h2 className="section-title">Tipo de notificação</h2>
          ) : (
            <div className="campo">
              <label className="label-notificacao">Tipo de notificação</label>
            </div>
          )}

          <div className={isCoordenador ? "field" : "campo"}>
            <div className="opcoes-bolinha opcoes-bolinha--compacta">
              <div
                className="item-bolinha"
                onClick={() => {
                  setNotificacoes((prev) => {
                    const next = { ...prev, calendario: !prev.calendario };
                    if (!next.calendario) {
                      setStartDate(null);
                      // Funcionalidades só existem com calendário.
                      setConfirmacaoPresenca(false);
                    }
                    return next;
                  });
                }}
                role="checkbox"
                aria-checked={notificacoes.calendario}
                tabIndex={0}
              >
                <div
                  className={`circular-check ${
                    notificacoes.calendario ? "active" : ""
                  }`}
                />
                <span>Aviso no calendário</span>
              </div>

              <div
                className="item-bolinha"
                onClick={() =>
                  setNotificacoes((prev) => ({
                    ...prev,
                    destaque: !prev.destaque,
                  }))
                }
                role="checkbox"
                aria-checked={notificacoes.destaque}
                tabIndex={0}
              >
                <div
                  className={`circular-check ${
                    notificacoes.destaque ? "active" : ""
                  }`}
                />
                <span>Aviso em destaque</span>
              </div>
            </div>
          </div>

          {notificacoes.calendario && (
            <div className={isCoordenador ? "field" : "campo"}>
              <label>Data do evento</label>

              <div className="linha-data-funcionalidades">
                <div className="input-calendario-wrapper">
                  <input
                    type="date"
                    className="input-estilizado"
                    value={startDate ?? ""}
                    min={new Date().toISOString().split("T")[0]}
                    onChange={(e) => setStartDate(e.target.value || null)}
                    onClick={(e) => e.target.showPicker?.()}
                  />
                </div>

                <button
                  type="button"
                  className="btn-funcionalidades"
                  onClick={() => setModalFuncAberto(true)}
                  aria-label="Funcionalidades do evento"
                  title="Funcionalidades do evento"
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>
          )}

          <div className="container-btn">
            <button className="btn-publicar" onClick={handleSalvar}>
              Publicar
            </button>
          </div>

          <ModalFuncionalidades
            aberto={modalFuncAberto}
            valorInicial={confirmacaoPresenca}
            onSalvar={(valor) => {
              setConfirmacaoPresenca(valor);
              setModalFuncAberto(false);
            }}
            onFechar={() => setModalFuncAberto(false)}
          />
        </section>
      </div>
    </div>
  );
};

export default CriarEvento;
